const utils = {
  escapeRegExp(s: string) : string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  },
  intoBuckets<T, Key>(
    items: ReadonlyArray<T>, keyGen: (item: T) => Key): Map<Key, ReadonlyArray<T>> {
      return utils.intoBucketsTransform(items, keyGen, item => item)
  },
  intoBucketsTransform<T, TT, Key>(
    items: ReadonlyArray<T>,
    keyGen: (item: T) => Key,
    transformer: (item: T) => TT,
  ): Map<Key, ReadonlyArray<TT>> {
      const result = new Map<Key, ReadonlyArray<TT>>()
      items.forEach(item => {
        const key: Key = keyGen(item)
        result.set(key, [...result.get(key) ?? [], transformer(item)])
      })
      return result
  },
  By<T, K>(keyGen: (item: T) => K, compare: (a: K, b: K) => number): (a: T, b: T) => number {
    /**
     * To be used as:
     * objects.sort(utils.By(obj => obj.field1, (a, b) => a.localeCompare(b)))
     */
    const keyGenMap = new Map<T, K>()
    const getAndCacheKey = (item: T): K => {
      if (keyGenMap.has(item)) {
        return keyGenMap.get(item)!
      }
      const key = keyGen(item)
      keyGenMap.set(item, key)
      return key
    }
    return (a: T, b: T) => {
      const keyA = getAndCacheKey(a)
      const keyB = getAndCacheKey(b)
      return compare(keyA, keyB)
    }
  },
  notNullOrUndefined<T>(val: T | null | undefined): val is T {
    return val !== null && val !== undefined
  },
  assert(condition: unknown, message?: string): asserts condition {
    if (!condition) throw new Error("Assertion failed: " + message);
  },
  isAreItems(count: number, singular: string, plural?: string): string {
    if (count === 1) {
      return `is ${count} ${singular}`
    }
    return `are ${count} ${plural ?? (singular + "s")}`
  },
  range(startEnd: number, end?: number, step?: number): ReadonlyArray<number> {
    const realStart = end === undefined ? 0 : startEnd
    const realEnd = end === undefined ? startEnd : end
    const realStep = step ?? 1
    const sign = realStep < 0 ? -1 : 1
    const result = new Array<number>()
    for (let i = realStart; sign * i < sign *realEnd; i += realStep) {
      result.push(i)
    }
    return result
  },
  asyncSleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  },
  keysOrUndefined<T>(item: T): (T extends undefined ? undefined : (keyof T)[]) {
    if (item === undefined) {
      return undefined as (T extends undefined ? undefined : (keyof T)[]);
    }
    return Object.keys(item) as (T extends undefined ? undefined : (keyof T)[]);
  },
  async asyncGeneratorGetAllGenerated<T>(
    ag: AsyncGenerator<T, void, void>
  ): Promise<ReadonlyArray<T>> {
    const result = new Array<T>()
    for await (const item of ag) {
      result.push(item)
    }
    return result
  },
  removeKeys<T, K extends ReadonlyArray<keyof T>>(
    obj: T, keys: K
  ): Omit<T, K extends Array<infer KEY> ?
  (KEY extends keyof T ? KEY : never) : never> {
    const newObj: Partial<T> = {...obj}
    for (const key of keys) {
      delete (newObj as T)[key]
    }
    return newObj as any
  }
}

// now some magic, totally unsure why it's needed, but it solves
// https://github.com/microsoft/TypeScript/issues/36931
const defaults: Omit<typeof utils, "assert"> & {assert: (condition: unknown, message?: string) => asserts condition} = utils
export default defaults
